import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Link from './SmartLink';

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/contact', label: 'Contact' },
];

export default function SharedNavbar() {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const mobileCta = (
    <Link to="/contact" className="w-full">
      <button className="w-full bg-gradient-to-r from-[#6366f1] via-[#8b5cf6] to-[#d946ef] text-white border-none px-5 py-3 rounded-xl text-[14px] font-bold cursor-pointer transition-opacity hover:opacity-[0.85] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]">
        Get in Touch
      </button>
    </Link>
  );

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 h-[70px] bg-[rgba(5,5,15,0.82)] backdrop-blur-xl border-b border-[rgba(99,102,241,0.15)]">
        <div className="relative flex h-full items-center justify-between px-4 sm:px-6 md:px-10">
          <button
            type="button"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(99,102,241,0.22)] bg-[rgba(255,255,255,0.04)] text-[#f1f1ff] transition-all hover:border-[#6366f1] hover:bg-[rgba(99,102,241,0.12)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6366f1] md:hidden"
          >
            <span className="relative block h-4 w-[18px]">
              <span className={`absolute left-0 top-0 h-0.5 w-full rounded-full bg-current transition-all ${mobileOpen ? 'translate-y-[7px] rotate-45' : ''}`} />
              <span className={`absolute left-0 top-[7px] h-0.5 w-full rounded-full bg-current transition-all ${mobileOpen ? 'opacity-0' : ''}`} />
              <span className={`absolute left-0 top-[14px] h-0.5 w-full rounded-full bg-current transition-all ${mobileOpen ? '-translate-y-[7px] -rotate-45' : ''}`} />
            </span>
          </button>

          <Link
            to="/"
            className="absolute left-1/2 flex -translate-x-1/2 items-center gap-2 text-[18px] font-black tracking-[-0.04em] text-[#f1f1ff] no-underline md:static md:translate-x-0 md:text-[19px]"
          >
            <img
              src="/trybinc_logo.png"
              alt="tryBinC"
              className="h-7 w-auto sm:h-8 md:h-auto"
              style={{ height: 28 }}
            />
            tryBinC
          </Link>

          <ul className="hidden list-none gap-7 md:flex">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`text-[14px] font-medium no-underline pb-[4px] border-b-2 transition-colors ${
                    pathname === item.to
                      ? 'text-[#f1f1ff] border-[#6366f1]'
                      : 'text-[#8888bb] border-transparent hover:text-[#f1f1ff]'
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="hidden md:flex md:items-center md:gap-2.5">
            <Link to="/contact">
              <button className="bg-gradient-to-r from-[#6366f1] via-[#8b5cf6] to-[#d946ef] text-white border-none px-[22px] py-[9px] rounded-lg text-[13.5px] font-bold cursor-pointer transition-opacity hover:opacity-[0.85] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]">
                Get in Touch
              </button>
            </Link>
          </div>

          <div className="h-11 w-11 md:hidden" aria-hidden="true" />
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-40 bg-[rgba(5,5,15,0.34)] backdrop-blur-[2px] transition-opacity duration-300 md:hidden ${
          mobileOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setMobileOpen(false)}
      />

      <aside
        className={`fixed top-[70px] left-0 z-50 h-[calc(100vh-70px)] w-[min(82vw,340px)] border-r border-[rgba(99,102,241,0.18)] bg-[linear-gradient(180deg,rgba(10,10,30,0.88),rgba(5,5,15,0.78))] backdrop-blur-2xl shadow-[0_24px_80px_rgba(0,0,0,0.45)] transition-transform duration-300 md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col px-5 pb-6 pt-6">
          <div className="mb-6 text-[11px] font-bold uppercase tracking-[0.14em] text-[#8888bb]">
            Navigation
          </div>

          <div className="flex flex-col gap-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-2xl border px-4 py-3.5 text-[15px] font-semibold no-underline transition-all ${
                  pathname === item.to
                    ? 'border-[rgba(99,102,241,0.35)] bg-[rgba(99,102,241,0.16)] text-[#f1f1ff]'
                    : 'border-transparent bg-[rgba(255,255,255,0.03)] text-[#b4b4d6] hover:border-[rgba(99,102,241,0.22)] hover:bg-[rgba(99,102,241,0.08)] hover:text-[#f1f1ff]'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="mt-auto flex flex-col gap-3 border-t border-[rgba(99,102,241,0.14)] pt-6">
            {mobileCta}
          </div>
        </div>
      </aside>
    </>
  );
}
